import Chip from '@components/Chip';
import { alpha, css, Divider, List, ListItem, ListItemButton, ListItemText, styled } from '@mui/material';
import MUITypography from '@mui/material/Typography';
import type { Configuration } from '@services/productInventory';
import { Link } from 'react-router-dom';
import { SideNavBase } from './SideNavBase';

interface NavLink {
  name: string;
  text: string;
  href: string;
  disabled?: boolean;
}

export type ConfigurationStatus = Configuration['status'] | 'UPDATING';

interface SideNavProps {
  title: string;
  status: ConfigurationStatus;
  selected: string;
  links: NavLink[];
}

export function SideNavManage({ title, status, selected, links }: Readonly<SideNavProps>) {
  const statusMap: Record<string, { label: string; color: 'primary' | 'secondary' | 'success' }> = {
    DRAFT: { label: 'DRAFT', color: 'secondary' },
    COMPLETED: { label: 'LIVE', color: 'success' },
    UPDATING: { label: 'UPDATING', color: 'primary' },
    UPDATING_PARTS_CATEGORIES: { label: 'UPDATING', color: 'primary' },
  };

  const { label, color } = statusMap[status] ?? statusMap.COMPLETED;

  return (
    <SideNavBase>
      <MenuHeader>{title}</MenuHeader>
      <Chip label={label} color={color} />
      <MenuDivider />
      <nav style={{ width: '100%' }}>
        <List>
          {links.map(({ name, text, href, disabled }) => {
            const buttonProps = {
              LinkComponent: Link,
              to: href,
              selected: name === selected,
              disabled,
            };
            return (
              <ListItem disablePadding key={href}>
                <MenuButton {...buttonProps} data-testid={`menu-${name}`}>
                  <ListItemText primary={text} />
                </MenuButton>
              </ListItem>
            );
          })}
        </List>
      </nav>
    </SideNavBase>
  );
}

const MenuDivider = styled(Divider)`
  border-top: 0;
  width: 100%;
  padding-top: 1rem;
  ${({ theme }) => css`
    border-bottom: 1px solid ${alpha(theme.palette.primary.main, 0.3)};
  `}
`;

const MenuButton = styled(ListItemButton)`
  ${({ theme }) => css`
    &.Mui-selected,
    &.Mui-selected:hover,
    &:focus,
    &:hover {
      border-right: 4px solid ${theme.palette.secondary.main};
      background-color: transparent;
      background-image: linear-gradient(
        to right,
        transparent,
        transparent,
        transparent,
        ${alpha(theme.palette.secondary.main, 0.3)}
      );
    }

    &.Mui-selected .MuiTypography-root {
      font-weight: bold;
    }
  `}
`;

const MenuHeader = styled(MUITypography)`
  ${({ theme }) => css`
    font-size: ${theme.typography.h6.fontSize};
    font-weight: ${theme.typography.fontWeightBold};
    word-break: break-word;
    padding-right: ${theme.spacing(2)};
  `}
`;
